import { useEffect, useState } from 'react';
import { IDataRequestsByType, IDataRequestSummary } from '@vivli/features/data-requests/infrastructure/interface';
import { DataRequestsPageComponent } from '@vivli/features/data-requests/components';
import { useActiveUserContext, useDataRequestFiltersContext, useDataRequestsApiContext } from '@vivli/core/infrastructure/context';
import { ITab, IUser } from '@vivli/shared/infrastructure/interface';
import { useDataRequestDefaultTabHook, useDataRequestSummaries } from '@vivli/features/data-requests/infrastructure/hook';
import { useNavigate, useParams } from 'react-router';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';

enum TabTitleEnum {
    awaitingMyAction = 'Awaiting My Action',
    draft = 'Draft',
    active = 'Active',
    notApproved = 'Not Approved',
    withdrawn = 'Withdrawn',
    archived = 'Archived'
}

enum TabRouteEnum {
    archived = 'archived',
    withdrawn = 'withdrawn',
    notApproved = 'notapproved',
    active = 'active',
    draft = 'draft',
    awaitingMyAction = 'awaitingmyaction'
}

const getTabs = (user: IUser, isPresumedResearcher?: boolean, requestsByType?: IDataRequestsByType) => {
    const awaitingMyActionTab: ITab<IDataRequestSummary[]> = {
        id: TabRouteEnum.awaitingMyAction,
        sortOrder: 0,
        title: TabTitleEnum.awaitingMyAction,
        data: requestsByType?.awaitingUserAction,
        count: requestsByType?.awaitingUserAction?.length
    };

    const defaultTabs: ITab<IDataRequestSummary[]>[] = [
        {
            id: TabRouteEnum.draft,
            sortOrder: 1,
            title: TabTitleEnum.draft,
            hint: isPresumedResearcher ? 'Requests you have not submitted yet' : 'Requests not yet submitted',
            data: requestsByType?.draftRequests,
            count: requestsByType?.draftRequests?.length,
            initialSortField: 'createdTime',
            initialSortOrder: 'desc'
        },
        {
            id: TabRouteEnum.active,
            sortOrder: 2,
            title: TabTitleEnum.active,
            hint: isPresumedResearcher
                ? 'Approved requests, and requests awaiting approvals, data upload, or resubmission'
                : 'Requests awaiting approvals or Data Package upload',
            data: requestsByType?.active,
            count: requestsByType?.active?.length
        },
        {
            id: TabRouteEnum.notApproved,
            sortOrder: 4,
            title: TabTitleEnum.notApproved,
            data: requestsByType?.notApproved,
            count: requestsByType?.notApproved?.length
        },
        {
            id: TabRouteEnum.withdrawn,
            sortOrder: 5,
            title: TabTitleEnum.withdrawn,
            data: requestsByType?.withdrawn,
            count: requestsByType?.withdrawn?.length
        },
        {
            id: TabRouteEnum.archived,
            sortOrder: 6,
            title: TabTitleEnum.archived,
            data: requestsByType?.archived,
            count: requestsByType?.archived?.length
        }
    ];

    //if not is presumed researcher, add awaiting my action tab to the beginning of the tabs array
    if (!isPresumedResearcher) {
        defaultTabs.unshift(awaitingMyActionTab);
    }
    return defaultTabs.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const DataRequestsFeature = () => {
    const [subHeader, setSubHeader] = useState<string>();
    const [tabs, setTabs] = useState<ITab<IDataRequestSummary[]>[]>();
    const [defaultTab, setDefaultTab] = useState<string>();
    const {user} = useActiveUserContext();
    const { isPresumedResearcher } = useUserPermissions();
    const {dataRequestSummaries} = useDataRequestSummaries();
    const { getDataRequest } = useDataRequestsApiContext();
    const navigate = useNavigate();
    const { status } = useParams();
    const { organizations } = useOrganizations();
    const { getDefaultTabRoute } = useDataRequestDefaultTabHook();
    const dataRequestFilters = useDataRequestFiltersContext();

    const getDefaultRequestsTab = () => (isPresumedResearcher ? TabRouteEnum.draft : TabRouteEnum.awaitingMyAction);

    const initDefaultTab = () => {
        const tabRoute = status?.toLowerCase();
        const nextDefaultTab = tabRoute?.length ? tabRoute : getDefaultRequestsTab();

        setDefaultTab(nextDefaultTab);

        if (!tabRoute) {
            navigate(`/admin/requests/${nextDefaultTab}`, { replace: true });
        }
    };

    const handleRowClick = (dataRequestSummary: IDataRequestSummary) => {
        getDataRequest(dataRequestSummary.id).subscribe({
            next: (dataRequest) => {
                const defaultRoute = getDefaultTabRoute(dataRequest, organizations, status);
                navigate(defaultRoute);
            }
        });
    };

    const handleTabClick = (tab: ITab<IDataRequestSummary[]>) => {
        navigate(`/admin/requests/${tab.id}`);
    };

    useEffect(() => {
        initDefaultTab();
        setTabs(getTabs(user, isPresumedResearcher));
    }, [status, user, isPresumedResearcher]);

    useEffect(() => {
        if (!user) {
            return;
        }
        //TODO PERMFE - redo with permissions+
        const subHeaderStr = 'Data Requests for studies provided by Vivli Members';

        setSubHeader(subHeaderStr);
    }, [user]);

    useEffect(() => {
        if (!dataRequestSummaries) {
            return;
        }

        const requestsByType = dataRequestFilters.filterAllRequestsByType(dataRequestSummaries, user);
        const updatedTabs = getTabs(user, isPresumedResearcher, requestsByType);

        setTabs(updatedTabs);
    }, [dataRequestSummaries]);

    return (
        <DataRequestsPageComponent
            title="Data Requests"
            isPresumedResearcher={isPresumedResearcher}
            subHeader={subHeader}
            tabs={tabs}
            defaultTab={defaultTab}
            showExport={true}
            onRowClick={handleRowClick}
            onTabClick={handleTabClick}
            hideFloatingFilter={true}
        />
    );
};
